import { useState, type FormEvent } from "react";
import { siteBrand } from "@/mocks/news";

const SUBMIT_URL = "https://readdy.ai/api/form/dahhng3oh653ivfvnvs0";

interface FormResponse {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
}

const topicOptions = ["剧集动态", "综艺录制", "音乐现场", "电影资讯", "版权合约", "品牌合作"];

export default function SubscribeSection() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [messageLength, setMessageLength] = useState(0);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      if (typeof value === "string" && value.trim() !== "") {
        payload.append(key, value);
      }
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        setFormError("");
        form.reset();
        setMessageLength(0);
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交没有成功，请稍后再试一次。");
      }
    } catch {
      setStatus("error");
      setFormError("网络好像不太稳定，请稍后再试一次。");
    }
  };

  return (
    <section id="subscribe" className="w-full border-y border-background-200 bg-primary-50">
      <div className="mx-auto grid w-full max-w-[1400px] grid-cols-1 gap-8 px-4 py-12 md:px-6 md:py-16 lg:grid-cols-12 lg:gap-12">
        <div className="lg:col-span-5">
          <span className="text-xs font-semibold tracking-widest text-primary-600">SUBSCRIBE</span>
          <h2 className="mt-2 font-heading text-2xl font-bold tracking-tight text-foreground-950 md:text-3xl">
            热点订阅
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-foreground-700">
            留下你的邮箱，我们会把你关注方向的进展整理后定时送达。也可以直接把想跟进的话题写在备注里，编辑部会优先整理。
          </p>
          <div className="mt-5 flex items-center gap-3 rounded-lg border border-primary-200 bg-background-50 p-4">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-500 text-background-50">
              <i className="ri-notification-3-line text-lg"></i>
            </span>
            <p className="text-sm text-foreground-700">
              目前已有读者在跟进 <span className="font-semibold text-primary-700">6 个分类</span> 的进展，平均每天整理 10 条以上更新。
            </p>
          </div>
          <p className="mt-4 text-xs text-foreground-500">
            也可以直接发邮件到{" "}
            <a href={`mailto:${siteBrand.email}`} className="cursor-pointer text-primary-700 underline-offset-4 hover:underline">
              {siteBrand.email}
            </a>
          </p>
        </div>

        <div className="lg:col-span-7">
          <form
            id="hotspot-subscribe-form"
            data-readdy-form
            onSubmit={handleSubmit}
            className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
            noValidate
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label htmlFor="sub-email" className="block text-sm font-medium text-foreground-800">
                  邮箱 <span className="text-primary-600">*</span>
                </label>
                <input
                  id="sub-email"
                  type="email"
                  name="email"
                  required
                  autoComplete="email"
                  placeholder="you@example.com"
                  className="mt-2 w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
                />
              </div>

              <div>
                <label htmlFor="sub-topic" className="block text-sm font-medium text-foreground-800">
                  关注方向
                </label>
                <select
                  id="sub-topic"
                  name="topic"
                  defaultValue="剧集动态"
                  className="mt-2 w-full cursor-pointer rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
                >
                  {topicOptions.map((topic) => (
                    <option key={topic} value={topic}>
                      {topic}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="sub-frequency" className="block text-sm font-medium text-foreground-800">
                  推送频率
                </label>
                <select
                  id="sub-frequency"
                  name="frequency"
                  defaultValue="每日一次"
                  className="mt-2 w-full cursor-pointer rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
                >
                  <option value="每日一次">每日一次</option>
                  <option value="每周一次">每周一次</option>
                  <option value="仅重大进展">仅重大进展</option>
                </select>
              </div>

              <div className="sm:col-span-2">
                <label htmlFor="sub-message" className="flex items-center justify-between text-sm font-medium text-foreground-800">
                  <span>想跟进的话题（选填）</span>
                  <span className="text-xs font-normal text-foreground-500">{messageLength}/500</span>
                </label>
                <textarea
                  id="sub-message"
                  name="message"
                  rows={3}
                  maxLength={500}
                  onChange={(e) => setMessageLength(e.target.value.length)}
                  placeholder="例如：希望跟进某档综艺的赛制变化"
                  className="mt-2 w-full resize-none rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
                ></textarea>
              </div>
            </div>

            <div className="contact-alt-field" aria-hidden="true">
              <label htmlFor="sub-website-alt">备选联系方式</label>
              <input
                id="sub-website-alt"
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                readOnly
              />
            </div>

            <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <button
                type="submit"
                disabled={status === "submitting"}
                className="flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <i className={status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-fill"}></i>
                {status === "submitting" ? "提交中…" : "提交订阅"}
              </button>
              <p className="text-xs text-foreground-500">提交即表示你愿意接收我们整理的热点进展。</p>
            </div>

            {status === "success" && (
              <p
                role="status"
                className="mt-4 flex items-center gap-2 rounded-md border border-secondary-200 bg-secondary-50 px-4 py-3 text-sm text-secondary-900"
              >
                <i className="ri-checkbox-circle-line text-base"></i>
                订阅成功，我们会把你关注方向的进展整理好发到你的邮箱。
              </p>
            )}
            {status === "error" && (
              <p
                role="alert"
                className="mt-4 flex items-center gap-2 rounded-md border border-primary-200 bg-primary-50 px-4 py-3 text-sm text-primary-800"
              >
                <i className="ri-error-warning-line text-base"></i>
                {formError}
              </p>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}